/**
 * Data Synchronization Service for Cross-Device Sync
 * Handles synchronization of game data, statistics, and preferences across devices
 */

import {
  Profile,
  GameStatistics,
  UserPreferences,
  GameResult,
  GameSession,
  AIDifficulty,
  GameMode,
  GameStatus,
  PlayerType
} from '../types';
import { LocalStorageManager } from './LocalStorageManager';
import { ProfileManager } from './ProfileManager';
import { UserManagementAPI, StatisticsAPI } from '../aws/apiGateway';

/**
 * Synchronization conflict resolution strategies
 */
export enum ConflictResolutionStrategy {
  SERVER_WINS = 'server_wins',
  CLIENT_WINS = 'client_wins',
  MERGE_ADDITIVE = 'merge_additive',
  LATEST_TIMESTAMP = 'latest_timestamp'
}

/**
 * Synchronization status
 */
export interface SyncStatus {
  isOnline: boolean;
  lastSyncTime: Date | null;
  pendingChanges: number;
  syncInProgress: boolean;
  lastError: string | null;
}

/**
 * Synchronization result
 */
export interface SyncResult {
  success: boolean;
  conflictsResolved: number;
  dataUpdated: boolean;
  error?: string;
  syncedAt: Date;
}

/**
 * Pending synchronization data
 */
interface PendingSyncData {
  statistics: GameStatistics;
  preferences: UserPreferences;
  gameResults: GameResult[];
  offlineGames: GameSession[];
  timestamp: Date;
}

/**
 * Data Synchronization Service
 * Manages cross-device data synchronization with conflict resolution
 */
export class DataSynchronizationService {
  private static readonly SYNC_STORAGE_KEY = 'tic-tac-toe-sync-data';
  private static readonly SYNC_STATUS_KEY = 'tic-tac-toe-sync-status';
  private static readonly SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

  private localStorageManager: LocalStorageManager;
  private profileManager: ProfileManager;
  private syncInterval: NodeJS.Timeout | null = null;
  private syncStatus: SyncStatus;

  constructor(profileManager: ProfileManager) {
    this.localStorageManager = new LocalStorageManager();
    this.profileManager = profileManager;
    this.syncStatus = this.loadSyncStatus();
    
    // Start automatic sync if user is logged in
    this.initializeAutoSync();
    
    // Listen for online/offline events
    this.setupNetworkListeners();
  }

  /**
   * Perform full data synchronization
   */
  async synchronizeData(strategy: ConflictResolutionStrategy = ConflictResolutionStrategy.MERGE_ADDITIVE): Promise<SyncResult> {
    const currentProfile = this.profileManager.getCurrentProfile();
    
    if (!currentProfile) {
      return {
        success: false,
        conflictsResolved: 0,
        dataUpdated: false,
        error: 'No user profile available for synchronization',
        syncedAt: new Date()
      };
    }

    this.updateSyncStatus({ syncInProgress: true, lastError: null });

    try {
      // Check network connectivity
      if (!navigator.onLine) {
        throw new Error('No network connection available');
      }

      // Get local data
      const localData = this.collectLocalData();
      
      // Get server data
      const serverData = await this.fetchServerData(currentProfile.id);
      
      if (!serverData) {
        // First sync - upload local data to server
        await this.uploadLocalDataToServer(currentProfile.id, localData);
        
        const result: SyncResult = {
          success: true,
          conflictsResolved: 0,
          dataUpdated: true,
          syncedAt: new Date()
        };
        
        this.updateSyncStatus({
          syncInProgress: false,
          lastSyncTime: result.syncedAt,
          pendingChanges: 0
        });
        
        return result;
      }

      // Resolve conflicts and merge data
      const mergedData = await this.resolveConflicts(localData, serverData, strategy);
      
      // Update local storage with merged data
      await this.updateLocalData(mergedData.local);
      
      // Update server with merged data
      await this.updateServerData(currentProfile.id, mergedData.server);
      
      const result: SyncResult = {
        success: true,
        conflictsResolved: mergedData.conflictsResolved,
        dataUpdated: mergedData.dataUpdated,
        syncedAt: new Date()
      };
      
      this.updateSyncStatus({
        syncInProgress: false,
        lastSyncTime: result.syncedAt,
        pendingChanges: 0
      });
      
      return result;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown synchronization error';
      
      this.updateSyncStatus({
        syncInProgress: false,
        lastError: errorMessage
      });
      
      return {
        success: false,
        conflictsResolved: 0,
        dataUpdated: false,
        error: errorMessage,
        syncedAt: new Date()
      };
    }
  }

  /**
   * Sync statistics only (lightweight sync)
   */
  async syncStatistics(): Promise<boolean> {
    const currentProfile = this.profileManager.getCurrentProfile();
    
    if (!currentProfile || !navigator.onLine) {
      return false;
    }

    try {
      const localStats = this.localStorageManager.getStatistics();
      const serverStats = await UserManagementAPI.getUserStatistics(currentProfile.id);
      
      if (serverStats) {
        // Merge statistics using additive strategy
        const mergedStats = this.mergeStatistics(localStats, serverStats);
        
        // Update both local and server
        this.localStorageManager.saveStatistics(mergedStats);
        // Update server statistics through profile sync
        const profile = await UserManagementAPI.getUserProfile(currentProfile.id);
        if (profile) {
          profile.statistics = mergedStats;
          await UserManagementAPI.updateUserProfile(currentProfile.id, profile);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to sync statistics:', error);
      return false;
    }
  }

  /**
   * Handle offline game synchronization when coming back online
   */
  async syncOfflineGames(): Promise<number> {
    const currentProfile = this.profileManager.getCurrentProfile();
    
    if (!currentProfile || !navigator.onLine) {
      return 0;
    }

    try {
      const offlineGames = this.localStorageManager.getOfflineGames();
      let syncedCount = 0;
      
      for (const game of offlineGames) {
        // Only sync completed games
        if (game.status === 'completed') {
          const gameResult: GameResult = {
            gameId: game.id,
            winner: this.determineWinner(game) || undefined,
            isDraw: this.isDraw(game),
            mode: game.mode.toString(),
            difficulty: game.players.find(p => p.type === PlayerType.AI)?.difficulty?.toString(),
            duration: this.calculateDuration(game),
            completedAt: game.createdAt
          };
          
          // Record game result on server
          const success = await UserManagementAPI.recordGameResult(currentProfile.id, gameResult);
          
          if (success) {
            // Remove from offline storage
            this.localStorageManager.removeOfflineGame(game.id);
            syncedCount++;
          }
        }
      }
      
      return syncedCount;
    } catch (error) {
      console.error('Failed to sync offline games:', error);
      return 0;
    }
  }

  /**
   * Get current synchronization status
   */
  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  /**
   * Force immediate synchronization
   */
  async forceSynchronization(): Promise<SyncResult> {
    return this.synchronizeData(ConflictResolutionStrategy.MERGE_ADDITIVE);
  }

  /**
   * Enable/disable automatic synchronization
   */
  setAutoSyncEnabled(enabled: boolean): void {
    if (enabled && !this.syncInterval) {
      this.syncInterval = setInterval(() => {
        this.performBackgroundSync();
      }, DataSynchronizationService.SYNC_INTERVAL);
    } else if (!enabled && this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Collect all local data for synchronization
   */
  private collectLocalData(): PendingSyncData {
    return {
      statistics: this.localStorageManager.getStatistics(),
      preferences: this.localStorageManager.getPreferences(),
      gameResults: [], // Would be populated from a game results cache
      offlineGames: this.localStorageManager.getOfflineGames(),
      timestamp: new Date()
    };
  }

  /**
   * Fetch server data for synchronization
   */
  private async fetchServerData(userId: string): Promise<PendingSyncData | null> {
    try {
      const [profile, statistics] = await Promise.all([
        UserManagementAPI.getUserProfile(userId),
        UserManagementAPI.getUserStatistics(userId)
      ]);
      
      if (!profile || !statistics) {
        return null;
      }
      
      return {
        statistics,
        preferences: profile.preferences,
        gameResults: [], // Would fetch from server
        offlineGames: [],
        timestamp: profile.createdAt
      };
    } catch (error) {
      console.error('Failed to fetch server data:', error);
      return null;
    }
  }

  /**
   * Resolve conflicts between local and server data
   */
  private async resolveConflicts(
    localData: PendingSyncData,
    serverData: PendingSyncData,
    strategy: ConflictResolutionStrategy
  ): Promise<{
    local: PendingSyncData;
    server: PendingSyncData;
    conflictsResolved: number;
    dataUpdated: boolean;
  }> {
    let conflictsResolved = 0;
    let dataUpdated = false;
    
    const resolvedLocal = { ...localData };
    const resolvedServer = { ...serverData };
    
    // Resolve statistics conflicts
    const statsConflict = this.hasStatisticsConflict(localData.statistics, serverData.statistics);
    if (statsConflict) {
      conflictsResolved++;
      dataUpdated = true;
      
      switch (strategy) {
        case ConflictResolutionStrategy.MERGE_ADDITIVE:
          const mergedStats = this.mergeStatistics(localData.statistics, serverData.statistics);
          resolvedLocal.statistics = mergedStats;
          resolvedServer.statistics = mergedStats;
          break;
          
        case ConflictResolutionStrategy.SERVER_WINS:
          resolvedLocal.statistics = serverData.statistics;
          break;
          
        case ConflictResolutionStrategy.CLIENT_WINS:
          resolvedServer.statistics = localData.statistics;
          break;
          
        case ConflictResolutionStrategy.LATEST_TIMESTAMP:
          if (localData.timestamp > serverData.timestamp) {
            resolvedServer.statistics = localData.statistics;
          } else {
            resolvedLocal.statistics = serverData.statistics;
          }
          break;
      }
    }
    
    // Resolve preferences conflicts
    const prefsConflict = this.hasPreferencesConflict(localData.preferences, serverData.preferences);
    if (prefsConflict) {
      conflictsResolved++;
      dataUpdated = true;
      
      switch (strategy) {
        case ConflictResolutionStrategy.MERGE_ADDITIVE:
        case ConflictResolutionStrategy.CLIENT_WINS:
          resolvedServer.preferences = localData.preferences;
          break;
          
        case ConflictResolutionStrategy.SERVER_WINS:
          resolvedLocal.preferences = serverData.preferences;
          break;
          
        case ConflictResolutionStrategy.LATEST_TIMESTAMP:
          if (localData.timestamp > serverData.timestamp) {
            resolvedServer.preferences = localData.preferences;
          } else {
            resolvedLocal.preferences = serverData.preferences;
          }
          break;
      }
    }
    
    return {
      local: resolvedLocal,
      server: resolvedServer,
      conflictsResolved,
      dataUpdated
    };
  }

  /**
   * Check if statistics have conflicts
   */
  private hasStatisticsConflict(local: GameStatistics, server: GameStatistics): boolean {
    return local.totalGames !== server.totalGames ||
           local.winRate !== server.winRate ||
           JSON.stringify(local.singlePlayer) !== JSON.stringify(server.singlePlayer) ||
           JSON.stringify(local.multiplayer) !== JSON.stringify(server.multiplayer);
  }

  /**
   * Check if preferences have conflicts
   */
  private hasPreferencesConflict(local: UserPreferences, server: UserPreferences): boolean {
    return JSON.stringify(local) !== JSON.stringify(server);
  }

  /**
   * Merge statistics using additive strategy
   */
  private mergeStatistics(local: GameStatistics, server: GameStatistics): GameStatistics {
    const merged: GameStatistics = {
      singlePlayer: {
        [AIDifficulty.EASY]: {
          wins: Math.max(local.singlePlayer.easy.wins, server.singlePlayer.easy.wins),
          losses: Math.max(local.singlePlayer.easy.losses, server.singlePlayer.easy.losses),
          draws: Math.max(local.singlePlayer.easy.draws, server.singlePlayer.easy.draws)
        },
        [AIDifficulty.MEDIUM]: {
          wins: Math.max(local.singlePlayer.medium.wins, server.singlePlayer.medium.wins),
          losses: Math.max(local.singlePlayer.medium.losses, server.singlePlayer.medium.losses),
          draws: Math.max(local.singlePlayer.medium.draws, server.singlePlayer.medium.draws)
        },
        [AIDifficulty.HARD]: {
          wins: Math.max(local.singlePlayer.hard.wins, server.singlePlayer.hard.wins),
          losses: Math.max(local.singlePlayer.hard.losses, server.singlePlayer.hard.losses),
          draws: Math.max(local.singlePlayer.hard.draws, server.singlePlayer.hard.draws)
        }
      },
      multiplayer: {
        wins: Math.max(local.multiplayer.wins, server.multiplayer.wins),
        losses: Math.max(local.multiplayer.losses, server.multiplayer.losses),
        draws: Math.max(local.multiplayer.draws, server.multiplayer.draws)
      },
      totalGames: 0,
      winRate: 0
    };
    
    // Recalculate totals
    const totalWins = Object.values(merged.singlePlayer).reduce((sum, diff) => sum + diff.wins, 0) + merged.multiplayer.wins;
    merged.totalGames = Object.values(merged.singlePlayer).reduce((sum, diff) => sum + diff.wins + diff.losses + diff.draws, 0) + 
                       merged.multiplayer.wins + merged.multiplayer.losses + merged.multiplayer.draws;
    merged.winRate = merged.totalGames > 0 ? totalWins / merged.totalGames : 0;
    
    return merged;
  }

  /**
   * Update local data with synchronized data
   */
  private async updateLocalData(data: PendingSyncData): Promise<void> {
    this.localStorageManager.saveStatistics(data.statistics);
    this.localStorageManager.savePreferences(data.preferences);
    
    // Update current profile
    const currentProfile = this.profileManager.getCurrentProfile();
    if (currentProfile) {
      currentProfile.statistics = data.statistics;
      currentProfile.preferences = data.preferences;
      this.localStorageManager.saveProfile(currentProfile);
    }
  }

  /**
   * Update server data with synchronized data
   */
  private async updateServerData(userId: string, data: PendingSyncData): Promise<void> {
    const profile = await UserManagementAPI.getUserProfile(userId);
    if (profile) {
      profile.statistics = data.statistics;
      profile.preferences = data.preferences;
      await UserManagementAPI.updateUserProfile(userId, profile);
    }
  }

  /**
   * Upload local data to server (first sync)
   */
  private async uploadLocalDataToServer(userId: string, data: PendingSyncData): Promise<void> {
    await UserManagementAPI.syncUserData(userId, {
      statistics: data.statistics,
      preferences: data.preferences,
      offlineGames: data.offlineGames
    });
  }

  /**
   * Initialize automatic synchronization
   */
  private initializeAutoSync(): void {
    const currentProfile = this.profileManager.getCurrentProfile();
    if (currentProfile && navigator.onLine) {
      this.setAutoSyncEnabled(true);
    }
  }

  /**
   * Setup network event listeners
   */
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.updateSyncStatus({ isOnline: true });
      this.performBackgroundSync();
    });
    
    window.addEventListener('offline', () => {
      this.updateSyncStatus({ isOnline: false });
    });
    
    // Initial online status
    this.updateSyncStatus({ isOnline: navigator.onLine });
  }

  /**
   * Perform background synchronization
   */
  private async performBackgroundSync(): Promise<void> {
    try {
      // Only sync if user is logged in and online
      const currentProfile = this.profileManager.getCurrentProfile();
      if (!currentProfile || !navigator.onLine || this.syncStatus.syncInProgress) {
        return;
      }
      
      // Perform lightweight sync
      await this.syncStatistics();
      await this.syncOfflineGames();
      
    } catch (error) {
      console.error('Background sync failed:', error);
    }
  }

  /**
   * Update synchronization status
   */
  private updateSyncStatus(updates: Partial<SyncStatus>): void {
    this.syncStatus = { ...this.syncStatus, ...updates };
    this.saveSyncStatus();
  }

  /**
   * Load synchronization status from storage
   */
  private loadSyncStatus(): SyncStatus {
    try {
      const stored = localStorage.getItem(DataSynchronizationService.SYNC_STATUS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          isOnline: navigator.onLine,
          lastSyncTime: parsed.lastSyncTime ? new Date(parsed.lastSyncTime) : null,
          pendingChanges: parsed.pendingChanges || 0,
          syncInProgress: false, // Always reset on load
          lastError: parsed.lastError || null
        };
      }
    } catch (error) {
      console.warn('Failed to load sync status:', error);
    }
    
    return {
      isOnline: navigator.onLine,
      lastSyncTime: null,
      pendingChanges: 0,
      syncInProgress: false,
      lastError: null
    };
  }

  /**
   * Save synchronization status to storage
   */
  private saveSyncStatus(): void {
    try {
      localStorage.setItem(
        DataSynchronizationService.SYNC_STATUS_KEY,
        JSON.stringify({
          lastSyncTime: this.syncStatus.lastSyncTime?.toISOString(),
          pendingChanges: this.syncStatus.pendingChanges,
          lastError: this.syncStatus.lastError
        })
      );
    } catch (error) {
      console.warn('Failed to save sync status:', error);
    }
  }

  /**
   * Helper methods for game result processing
   */
  private determineWinner(game: GameSession): string | null {
    // Implementation would check game board for winner
    // For now, we'll check if the game is completed and determine winner from board state
    if (game.status === GameStatus.COMPLETED) {
      // This would analyze the board to determine the winner
      // For simplicity, returning null (draw) for now
      return null;
    }
    return null;
  }

  private isDraw(game: GameSession): boolean {
    // Implementation would check if game ended in draw
    return game.status === GameStatus.COMPLETED;
  }

  private calculateDuration(game: GameSession): number {
    // Implementation would calculate game duration
    // For now, return a default duration
    return 300; // 5 minutes default
  }
}